import { PropType } from './avroom/mutations';
import { mappingToSocketProcessorHandler, processSocketMessage, SocketEventType } from './socketMessageProcessor';

import { getEnv } from '@/constants';
import loggerService from '@/services/logger.service';
import { GatewayClient } from '@/services/socketClient/gatewayClient';
import trackerService from '@/services/tracker.service';
import { RootState } from '@/store';
import { replaceRoute } from '@/utils/routerAccess';
import { makeUser } from '@/utils/userTransform';

const socket = new GatewayClient(getEnv('VUE_APP_API_GATEWAY_URL'), loggerService);
interface ApiGatewayContext {
  state: ApigatewayState;
  rootState: RootState;
  rootGetters: { [k: string]: any };
  dispatch: (action: string, params?: any, settings?: { root: boolean }) => Promise<any>;
  commit: (mutation: string, payload?: any, config?: { root: boolean }) => any;
}

export interface ApigatewayState {
  socketstatus: 'offline' | 'online' | 'error';
  firstConnectDone: boolean;
  bailed: boolean;
}

const state: ApigatewayState = {
  socketstatus: 'offline',
  firstConnectDone: false,
  bailed: false
};

const mutations = {
  setSocketStatus(state: ApigatewayState, status: PropType<ApigatewayState, 'socketstatus'>) {
    state.socketstatus = status;
  },
  setFirstConnect(state: ApigatewayState, value: boolean) {
    state.firstConnectDone = value;
  },
  setBailed(state: ApigatewayState, value: boolean) {
    state.bailed = value;
  }
};

export default {
  namespaced: true,
  state,
  getters: {},
  mutations,
  actions: {
    processEvent(context: ApiGatewayContext, msg: any) {
      const eventType: SocketEventType = msg.event || '';
      if (mappingToSocketProcessorHandler[eventType]) {
        processSocketMessage(msg.data, msg.event, 'gatewaySockets');
        return;
      }
      switch (msg.event) {
        case 'user-updated':
          {
            const data = msg.data;
            if (data instanceof Array) {
              const users = data.reduce((acc, { user }) => {
                const madeUser = makeUser(user.event_user);
                acc[madeUser.id] = madeUser;
                return acc;
              }, {});
              context.commit('event/addUsersBulk', users, { root: true });
              // force update chats
              if (data.length > 0) {
                context.commit('event/invalidateEventNetwork', null, {
                  root: true
                });
              }
            } else {
              loggerService.error('Expected the payload of event to be an array');
            }
          }
          break;
      }
    },

    checkEventAndKick(context: ApiGatewayContext, reason: string) {
      const userStatus = context.rootState.user?.currentEventUser?.status;

      if (!userStatus) {
        loggerService.error('User status not found when checking event and kicking out');
        return;
      }

      if ((!userStatus?.isOrganizer && !userStatus?.registeredToEvent) || userStatus?.blocked) {
        return context.dispatch('kickOut', reason || 'Received 400 in checkEventAndKick');
      }
    },
    kickOut(context: ApiGatewayContext, reason: string) {
      context.dispatch('mattermost/disconnect', null, { root: true });
      context.dispatch('disconnect');
      loggerService.info('Kicking user out reason: ' + reason);
      context.commit('event/eventHeartbeat/stopEventHeartbeat');
      replaceRoute({
        name: 'ErrorPage403',
        query: {
          message: 'EVENT_ACCESS_NOT_ALLOWED'
        }
      });
      throw new Error('user kicked out from api-gateway');
    },
    disconnect(context: ApiGatewayContext) {
      context.state.firstConnectDone = false;
      socket.disconnect();
      socket.openPromise = null;
    },
    firstConnectIfNotConnected(context: ApiGatewayContext) {
      if (!context.state.firstConnectDone && !socket.openPromise) {
        trackerService.track('apigateway', 'connectionInitialized', { timestamp: Date.now() });
        return socket.connect();
      }
    },
    subscribeToEvent(context: ApiGatewayContext, payload: { eventId: string }) {
      loggerService.info('Subscribing to event: ', payload.eventId);
      if (context.rootState.user.currentUser?.id) {
        context.dispatch('firstConnectIfNotConnected');
        socket.sendMessage({
          message: 'setEventId',
          eventId: payload.eventId,
          userId: context.rootState.user.currentUser?.id,
          route: context.rootState.route.fullPath
        });
      } else {
        loggerService.warn('Userid not found when trying to subscribe to event. Retrying in 2 sec!');
        setTimeout(() => {
          context.dispatch('subscribeToEvent', payload);
        }, 2000);
      }
    },
    subscribeToBroadcast(context: ApiGatewayContext, payload: { broadcastId: string }) {
      loggerService.info('Subscribing to broadcast: ', payload.broadcastId);
      if (context.rootState.user.currentUser?.id) {
        context.dispatch('firstConnectIfNotConnected');
        socket.sendMessage({
          message: 'setEventId',
          eventId: payload.broadcastId,
          userId: context.rootState.user.currentUser?.id,
          route: context.rootState.route.fullPath
        });
      } else {
        loggerService.warn('Did not find userid when subscribing to broadcast. Retrying in 2 sec!');
        setTimeout(() => {
          context.dispatch('subscribeToBroadcast', payload);
        }, 2000);
      }
    },
    unsubscribeFromBroadcast(context: ApiGatewayContext, payload: { broadcastId: string }) {
      loggerService.info('Unsubscribing from broadcast: ', payload.broadcastId);
      if (context.rootState.user.currentUser?.id) {
        context.dispatch('firstConnectIfNotConnected');
        socket.sendMessage({
          message: 'deleteBroadcast',
          broadcastId: payload.broadcastId,
          userId: context.rootState.user.currentUser.id
        });
      } else {
        loggerService.warn('Did not find userid when unsubscribing from broadcast. Retrying in 2 sec!');
        setTimeout(() => {
          context.dispatch('unsubscribeFromBroadcast', payload);
        }, 2000);
      }
    }
  }
};
